import React from "react"
import {preparePhoneNumber} from "../../funcs"

export type CodeRequestStatus = "sending" | "sent" | "failed"

type HintContext = {
    channel: string
    phone_number: string
    reverseCallWaiting: boolean
    renderDialPhoneLink: () => React.ReactNode
    failureText?: string | null
}

function sendingHint(channel: string, phone: string): React.ReactNode {
    const formatted = preparePhoneNumber(phone)
    if (channel === "tg-gateway") {
        return <>Отправляем код в Telegram на номер <b>{formatted}</b>…</>
    }
    if (channel === "sms") {
        return <>Отправляем SMS с кодом на номер <b>{formatted}</b>…</>
    }
    if (channel === "whatsapp") {
        return <>Отправляем код в WhatsApp на номер <b>{formatted}</b>…</>
    }
    if (channel === "call") {
        return <>Запрашиваем звонок на номер <b>{formatted}</b>…</>
    }
    if (channel === "reverse-call") {
        return <>Получаем бесплатный номер для звонка с телефона <b>{formatted}</b>…</>
    }
    if (channel === "tg") {
        return <>Запрашиваем код через Telegram-бота для номера <b>{formatted}</b>…</>
    }
    if (channel === "max") {
        return <>Запрашиваем код через MAX для номера <b>{formatted}</b>…</>
    }
    return <>Отправляем код на номер <b>{formatted}</b>…</>
}

function sentHint(ctx: HintContext): React.ReactNode {
    const {channel, phone_number, reverseCallWaiting, renderDialPhoneLink} = ctx
    const formatted = preparePhoneNumber(phone_number)

    if (channel === "tg-gateway") {
        return (
            <div className="verification-code-v2__hint">
                <ol className="verification-code-v2__hint--numbered">
                    <li>Код отправлен вам в Telegram на номер <b>{formatted}</b>. Проверьте сообщение в вашем мессенджере.</li>
                    <li>Введите полученный код ниже.</li>
                </ol>
                <a href="https://t.me/VerificationCodes" target="_blank" rel="noopener noreferrer" className="verification-code-v2__hint-link">Перейти в Telegram</a>
            </div>
        )
    }
    if (channel === "sms") {
        return (
            <ol className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <li>На номер <b>{formatted}</b> отправлено сообщение с кодом. Введите его ниже.</li>
            </ol>
        )
    }
    if (channel === "whatsapp") {
        return (
            <ol className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <li>На номер <b>{formatted}</b> отправлено сообщение с кодом в WhatsApp от <b>OkiDoki</b>. Введите код ниже.</li>
            </ol>
        )
    }
    if (channel === "tg") {
        return (
            <ol className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <li>Для того, чтобы получить смс код в телеграме на номер <b>{formatted}</b>, перейдите по ссылке: <a href={`https://t.me/${process.env.REACT_APP_TG_AUTH_BOT_NAME}`} target="_blank" rel="noopener noreferrer"><b>@{process.env.REACT_APP_TG_AUTH_BOT_NAME}</b></a></li>
                <li>Запустите бота, нажав на соответствующую кнопку или командой старт <b>/start</b></li>
                <li>Затем <b>поделитесь своим контактом</b>, используя кнопку <b>"Выслать свой номер телефона"</b></li>
            </ol>
        )
    }
    if (channel === "max") {
        const maxAuthBotId = process.env.REACT_APP_MAX_AUTH_BOT_ID || "id6330100935_1_bot"
        const maxAuthBotLink = `https://max.ru/${maxAuthBotId}`
        return (
            <ol className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <li>Чтобы получить код в MAX на номер <b>{formatted}</b>, сначала перейдите в бота.</li>
                <li className="verification-code-v2__bot-step">
                    Запустите бота (если нужно — нажмите старт).
                    <a
                        href={maxAuthBotLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="verification-code-v2__bot-btn"
                    >
                        Перейти в бота
                    </a>
                </li>
                <li>Затем <b>поделитесь своим контактом</b>, используя кнопку <b>«Выслать свой номер телефона»</b>.</li>
            </ol>
        )
    }
    if (channel === "call") {
        return (
            <ol className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <li>Сейчас вам поступит телефонный звонок на номер <b>{formatted}</b>.</li>
                <li>Звонок можно отклонить. Ваш код — 4 последние цифры номера телефона, с которого вам позвонили.</li>
            </ol>
        )
    }
    if (channel === "reverse-call") {
        return (
            <ol className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <li>Позвоните <b>бесплатно</b> на номер {renderDialPhoneLink()} с телефона <b>{formatted}</b>.</li>
                <li>Звонок можно сбросить сразу после соединения — это бесплатно.</li>
                <li>После звонка код подставится автоматически, затем нажмите «Завершить».</li>
                {reverseCallWaiting && (
                    <li style={{listStyle: "none", marginTop: 8, display: "flex", alignItems: "center", gap: 8}}>
                        <span>Ожидаем ваш звонок…</span>
                    </li>
                )}
            </ol>
        )
    }
    return null
}

export function renderVerificationCodeHint(
    status: CodeRequestStatus,
    ctx: HintContext,
): React.ReactNode {
    if (status === "sending") {
        return (
            <div className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <p style={{margin: 0}}>{sendingHint(ctx.channel, ctx.phone_number)}</p>
            </div>
        )
    }

    if (status === "failed") {
        return (
            <div className="verification-code-v2__hint verification-code-v2__hint--numbered">
                <p style={{margin: 0}}>
                    {ctx.failureText || (
                        <>
                            Не удалось отправить код на номер <b>{preparePhoneNumber(ctx.phone_number)}</b>.
                            Проверьте интернет и нажмите «Выслать код ещё раз» или выберите другой способ ниже.
                        </>
                    )}
                </p>
            </div>
        )
    }

    return sentHint(ctx)
}
